import { useSessionKey } from "@auth/sessionStore";
import { useUser } from "@context/UserContext";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faAnglesLeft,
  faAnglesRight,
  faArrowDown,
  faArrowDownLeft,
  faArrowDownRight,
  faArrowLeft,
  faArrowRight,
  faCamera,
  faCameraCctv,
  faCircleCheck,
  faHeadSideMask,
  faTrash,
  faUpload,
  faUser,
  faVideo,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRoomsListQuery } from "@rooms/roomsQueries";
import {
  Button,
  ConfirmDialog,
  FieldError,
  IconButton,
  SettingsCard,
  StatusMark,
} from "@scribenote/brian-design-system";
import { Navigate } from "@tanstack/react-router";
import { GuidedPhotoCapture } from "@users/GuidedPhotoCapture";
import type {
  StaffPhoto,
  StaffPhotoPose,
  StaffPhotoPoseGuide,
} from "@users/staffPhotoClient";
import {
  useConfirmStaffPhoto,
  useConsentToRoomPhotos,
  useDeleteStaffPhoto,
  useMyStaffPhotos,
  useUploadStaffPhoto,
} from "@users/staffPhotoQueries";
import { formatDateTime } from "@utils/format-date";
import { useRef, useState } from "react";

/** Directions are the staff member's own, as the mirrored webcam preview shows them. */
const POSE_HINT: Record<StaffPhotoPose, IconDefinition> = {
  front: faUser,
  down_front: faArrowDown,
  down_left: faArrowDownLeft,
  down_right: faArrowDownRight,
  turn_left: faArrowLeft,
  turn_right: faArrowRight,
  work_gear: faHeadSideMask,
  profile_left: faAnglesLeft,
  profile_right: faAnglesRight,
  torso_front: faUser,
  torso_side: faArrowLeft,
  room_camera: faVideo,
  room_camera_far: faCameraCctv,
};

const Thumbnail = ({ photo, alt }: { photo: StaffPhoto; alt: string }) =>
  photo.imageUrl ? (
    <img
      src={photo.imageUrl}
      alt={alt}
      referrerPolicy="no-referrer"
      data-dd-privacy="hidden"
      className="aspect-[3/4] w-full rounded-paper border border-rule bg-recess object-contain"
    />
  ) : (
    <div className="aspect-[3/4] w-full rounded-paper border border-rule bg-recess" />
  );

const EmptySlot = ({ pose }: { pose: StaffPhotoPose }) => (
  <div className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-2 rounded-paper border border-dashed border-rule-strong bg-wall text-faint">
    <FontAwesomeIcon icon={faUser} className="text-3xl" aria-hidden="true" />
    {pose !== "front" && (
      <FontAwesomeIcon icon={POSE_HINT[pose]} aria-hidden="true" />
    )}
  </div>
);

const PoseSlot = ({
  guide,
  photo,
  disabled,
  onTake,
  onUpload,
  onDelete,
}: {
  guide: StaffPhotoPoseGuide;
  photo: StaffPhoto | undefined;
  disabled: boolean;
  onTake: () => void;
  onUpload: () => void;
  onDelete: (photo: StaffPhoto) => void;
}) => (
  <li className="flex min-w-0 flex-col gap-1.5">
    {photo ? (
      <Thumbnail photo={photo} alt={guide.label} />
    ) : (
      <EmptySlot pose={guide.pose} />
    )}
    <span className="truncate text-sm text-ink">{guide.label}</span>
    <div className="flex gap-1">
      <IconButton
        size="sm"
        variant="outline"
        aria-label={photo ? "Retake with webcam" : "Take with webcam"}
        disabled={disabled}
        onClick={onTake}
      >
        <FontAwesomeIcon icon={faCamera} aria-hidden="true" />
      </IconButton>
      <IconButton
        size="sm"
        variant="outline"
        aria-label="Upload a photo"
        disabled={disabled}
        onClick={onUpload}
      >
        <FontAwesomeIcon icon={faUpload} aria-hidden="true" />
      </IconButton>
      {photo && (
        <IconButton
          size="sm"
          variant="outline"
          aria-label="Delete"
          onClick={() => onDelete(photo)}
        >
          <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
        </IconButton>
      )}
    </div>
  </li>
);

const PendingSlot = ({
  label,
  photo,
  confirmDisabled,
  onConfirm,
  onDelete,
}: {
  label: string;
  photo: StaffPhoto;
  confirmDisabled: boolean;
  onConfirm: () => void;
  onDelete: () => void;
}) => (
  <li className="flex min-w-0 flex-col gap-1.5">
    <Thumbnail photo={photo} alt={label} />
    <span className="truncate text-sm text-ink">{label}</span>
    <span className="text-xs text-annotation">
      {`Taken ${formatDateTime(photo.createdAt)}`}
    </span>
    <div className="flex flex-wrap gap-1">
      <Button
        variant="accent"
        size="sm"
        disabled={confirmDisabled}
        onClick={onConfirm}
      >
        <FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" />
        Use this photo
      </Button>
      <Button variant="outline" size="sm" onClick={onDelete}>
        <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
        Delete
      </Button>
    </div>
  </li>
);

const RoomPhoto = ({
  photo,
  caption,
  onDelete,
}: {
  photo: StaffPhoto;
  caption?: string;
  onDelete: (photo: StaffPhoto) => void;
}) => (
  <li className="flex min-w-0 flex-col gap-1.5">
    <Thumbnail photo={photo} alt={caption ?? "Room camera photo"} />
    {caption && <span className="truncate text-sm text-ink">{caption}</span>}
    <span className="text-xs text-annotation">
      {`Taken ${formatDateTime(photo.createdAt)}`}
    </span>
    <IconButton
      size="sm"
      variant="outline"
      aria-label="Delete"
      className="self-start"
      onClick={() => onDelete(photo)}
    >
      <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
    </IconButton>
  </li>
);

const UNKNOWN_ROOM = "Earlier capture, room unknown";

/**
 * The staff member's own opt-in to reference photos, which Brian shows its
 * image model beside the room picture when it names the people in a room.
 * Consent is given once, covers the room-camera photos an admin takes, and is
 * the version brian returned with the text shown here; guided photos and
 * confirming a waiting photo send that version too.
 */
export const StaffPhotoPage = () => {
  const { session } = useUser();
  const sessionKey = useSessionKey();
  const query = useMyStaffPhotos(sessionKey);
  const upload = useUploadStaffPhoto();
  const confirm = useConfirmStaffPhoto();
  const remove = useDeleteStaffPhoto();
  const consent = useConsentToRoomPhotos();
  const rooms = useRoomsListQuery(sessionKey, undefined, true);
  const [capturing, setCapturing] = useState<StaffPhotoPoseGuide[] | null>(
    null,
  );
  const [deleting, setDeleting] = useState<StaffPhoto | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadPose = useRef<StaffPhotoPose | null>(null);

  if (!session) return <Navigate to="/" />;
  const mine = query.data;
  const guided = mine?.confirmed.filter((p) => p.source !== "camera") ?? [];
  const cameraPhotos =
    mine?.confirmed.filter((p) => p.source === "camera") ?? [];
  const byPose = new Map(guided.map((photo) => [photo.pose, photo] as const));
  const missing = mine?.poses.filter((guide) => !byPose.has(guide.pose)) ?? [];
  const taken = (mine?.poses.length ?? 0) - missing.length;
  const currentPoses = new Set(mine?.poses.map((guide) => guide.pose));
  const extraPhotos = guided.filter((photo) => !currentPoses.has(photo.pose));
  const roomName = (roomId: string | null) =>
    rooms.data?.find((room) => room.id === roomId)?.name ?? UNKNOWN_ROOM;
  const photosByRoom = new Map<string | null, StaffPhoto[]>();
  for (const photo of cameraPhotos)
    photosByRoom.set(photo.roomId, [
      ...(photosByRoom.get(photo.roomId) ?? []),
      photo,
    ]);
  const consented = mine?.consented ?? false;
  const writable = consented && !upload.isPending;

  const chooseFile = (pose: StaffPhotoPose) => {
    uploadPose.current = pose;
    fileInput.current?.click();
  };

  return (
    <>
      <section
        aria-label="Reference photos"
        className="flex min-w-0 flex-1 flex-col"
      >
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 rounded-[4px] border border-rule bg-card px-4 pt-7 pb-8 shadow-sheet sm:px-9">
          <SettingsCard
            title="My reference photos"
            subtitle="Photos from a few angles help Brian tell staff apart on the room cameras. This is optional, and only you can agree to it."
          >
            {query.isError && (
              <FieldError>I couldn’t load your reference photos.</FieldError>
            )}
            {mine && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-ink-body">{mine.consentText}</p>
                  {consented ? (
                    <StatusMark>You agreed</StatusMark>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        variant="accent"
                        size="sm"
                        disabled={consent.isPending}
                        onClick={() => consent.mutate(mine.consentVersion)}
                      >
                        I agree
                      </Button>
                      <span className="text-sm text-annotation">
                        Agreeing once covers every room-camera photo an admin
                        takes of you. Using a waiting photo below also records
                        it.
                      </span>
                    </div>
                  )}
                </div>
                {mine.pending.length > 0 && (
                  <div className="flex flex-col gap-3 border-t border-rule pt-4">
                    <StatusMark>Waiting for you</StatusMark>
                    <p className="text-sm text-ink-body">
                      {mine.pending.length === 1
                        ? "An admin took this photo from a room camera. Brian does not use it until you agree."
                        : "An admin took these photos from a room camera. Brian does not use them until you agree."}
                    </p>
                    <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                      {mine.pending.map((photo) => (
                        <PendingSlot
                          key={photo.id}
                          label={`Room camera: ${roomName(photo.roomId)}`}
                          photo={photo}
                          confirmDisabled={confirm.isPending}
                          onConfirm={() =>
                            confirm.mutate({
                              photoId: photo.id,
                              consentVersion: mine.consentVersion,
                            })
                          }
                          onDelete={() => setDeleting(photo)}
                        />
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex flex-col gap-3 border-t border-rule pt-4">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="flex flex-col gap-1">
                      <p className="font-display text-base font-medium text-ink">
                        {taken} of {mine.poses.length} guided photos
                      </p>
                      <p className="text-sm text-ink-body">
                        Take three face angles and two torso views, front and
                        side. An admin adds your photos from each room camera.
                      </p>
                    </div>
                    {missing.length > 0 && (
                      <Button
                        variant="accent"
                        size="sm"
                        disabled={!writable}
                        onClick={() => setCapturing(missing)}
                      >
                        <FontAwesomeIcon icon={faCamera} aria-hidden="true" />
                        {taken === 0
                          ? "Take all photos"
                          : "Take remaining photos"}
                      </Button>
                    )}
                  </div>
                  <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                    {mine.poses.map((guide) => (
                      <PoseSlot
                        key={guide.pose}
                        guide={guide}
                        photo={byPose.get(guide.pose)}
                        disabled={!writable}
                        onTake={() => setCapturing([guide])}
                        onUpload={() => chooseFile(guide.pose)}
                        onDelete={setDeleting}
                      />
                    ))}
                  </ul>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      const pose = uploadPose.current;
                      if (file && pose && consented)
                        upload.mutate({
                          file,
                          consentVersion: mine.consentVersion,
                          pose,
                          guided: false,
                        });
                      event.target.value = "";
                    }}
                  />
                </div>
                {extraPhotos.length > 0 && (
                  <div className="flex flex-col gap-3 border-t border-rule pt-4">
                    <StatusMark>Additional saved angles</StatusMark>
                    <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                      {extraPhotos.map((photo) => (
                        <RoomPhoto
                          key={photo.id}
                          photo={photo}
                          caption={photo.pose.replace(/_/g, " ")}
                          onDelete={setDeleting}
                        />
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex flex-col gap-3 border-t border-rule pt-4">
                  <div className="flex flex-col gap-1">
                    <p className="font-display text-base font-medium text-ink">
                      From a room camera
                    </p>
                    <p className="text-sm text-ink-body">
                      An admin crops your head and torso from each room camera
                      looking down at you, up to three photos per room. Once you
                      agree, they are used as soon as they are taken.
                    </p>
                  </div>
                  {photosByRoom.size === 0 ? (
                    <p className="text-sm text-annotation">
                      No room-camera photos of you yet.
                    </p>
                  ) : (
                    [...photosByRoom].map(([roomId, photos]) => (
                      <div
                        key={roomId ?? "unknown"}
                        className="flex flex-col gap-2"
                      >
                        <StatusMark>{roomName(roomId)}</StatusMark>
                        <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                          {photos.map((photo) => (
                            <RoomPhoto
                              key={photo.id}
                              photo={photo}
                              onDelete={setDeleting}
                            />
                          ))}
                        </ul>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </SettingsCard>
        </div>
      </section>
      {capturing && mine && (
        <GuidedPhotoCapture
          poses={capturing}
          consentVersion={mine.consentVersion}
          onClose={() => setCapturing(null)}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        onCancel={() => setDeleting(null)}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
        }
        title="Delete this photo?"
        destructive
        busy={remove.isPending}
        confirmLabel="Delete"
      >
        <p>Brian deletes the photo now and stops using it.</p>
      </ConfirmDialog>
    </>
  );
};
